import React from 'react';
import {
    Paper, Box, Typography, IconButton, Stack, Chip, Tooltip, Avatar, Divider
} from '@mui/material';
import { Edit, Delete, MedicalServices } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const PseudoDicomProfileCard = ({ profile, onEdit, onDelete }) => {
    const { t } = useTranslation();
    const { name, description, options = {} } = profile;

    const tags = options.tags_to_pseudonymize || [];
    
    // Group UIDs into a single chip to save space if all 3 are present
    const hasUIDs = tags.includes('SOPInstanceUID') && tags.includes('StudyInstanceUID') && tags.includes('SeriesInstanceUID');
    const displayTags = hasUIDs 
        ? [...tags.filter(t => !t.includes('UID')), 'All UIDs'] 
        : tags;

    const commonChipProps = {
        size: 'small',
        color: 'primary',
        variant: 'outlined',
        sx: { borderRadius: '6px', fontSize: 11, height: 24, fontWeight: 600 },
    };

    const rulesCountLabel = tags.length > 0
        ? t('DicomProfiles.tagsCount', '{{count}} DICOM Tagov', { count: tags.length })
        : t('DicomProfiles.tagsCountEmpty', 'Žiadne tagy');

    return (
        <Paper
            elevation={0}
            sx={(theme) => ({
                p: 2,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                transition: theme.transitions.create(
                    ['box-shadow', 'border-color', 'transform'],
                    { duration: theme.transitions.duration.shorter }
                ),
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: `0 4px 12px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.05)'}`,
                    transform: 'translateY(-2px)',
                },
            })}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                <Avatar
                    sx={{
                        width: 36, height: 36,
                        bgcolor: 'primary.main', color: 'primary.contrastText', flexShrink: 0,
                    }}
                >
                    <MedicalServices fontSize="small" />
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                        {name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }} noWrap>
                        {rulesCountLabel}
                    </Typography>

                    {description && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                            }}
                        >
                            {description}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', flexShrink: 0 }}>
                    <Tooltip title={t('common.edit', 'Upraviť profil')}>
                        <IconButton size="small" onClick={() => onEdit(profile)} sx={{ mr: 0.5, bgcolor: 'action.hover' }}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete', 'Odstrániť profil')}>
                        <IconButton size="small" onClick={() => onDelete(profile)} color="error" sx={{ bgcolor: 'action.hover' }}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* Tags (Chips) */}
            <Box sx={{ mt: 0.5 }}>
                {displayTags.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('DicomProfiles.noOptionsSummary', 'Bez špecifikovaných tagov.')}
                    </Typography>
                ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {/* Show up to 6 chips, then a "+X more" chip */}
                        {displayTags.slice(0, 6).map((tag, i) => (
                            <Chip key={i} label={tag} {...commonChipProps} />
                        ))}
                        {displayTags.length > 6 && (
                            <Chip 
                                label={`+${displayTags.length - 6}`} 
                                size="small" 
                                sx={{ borderRadius: '6px', fontSize: 11, height: 24, bgcolor: 'action.hover', fontWeight: 600 }} 
                            />
                        )}
                    </Stack>
                )}
            </Box>

            {/* DB Storage indicator */}
            {options.save_to_db && (
                <Typography variant="caption" color="success.main" sx={{ mt: 'auto', pt: 1, fontWeight: 600 }}>
                    ✓ {t('DicomProfiles.savesToDb', 'Ukladá súbory do DB')}
                </Typography>
            )}
        </Paper>
    );
};

export default PseudoDicomProfileCard;